import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiSend } from "@mdi/js";
import LoadingSpinner from "app/components/LoadingSpinner";
import CustomToast from "app/components/common/CustomToast";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import UpgradePlanModal from "app/components/WebApp/GlobalNavBar/UpgradePlanModal";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import AppointmentModal from "app/containers/JobCalendar/components/AppointmentModal";
import JobSetupModal from "app/containers/JobCalendar/components/JobSetupModal";
import DeleteConfirmationModal from "app/containers/JobCalendar/components/DeleteConfirmationModal";
import ConfirmationModal from "app/components/common/ConfirmationModal";
import ContractReasonModal from "./ContractReasonModal";
import {
  fetchCalendarSettings,
  fetchSectionTemplates,
} from "app/containers/JobCalendar/slice";
import {
  computeContractTrack,
  computePaymentTrack,
} from "app/containers/JobCalendar/utils/sendTrackRules";
import { buildSendMessages } from "app/containers/JobCalendar/utils/sendFeedback";
import {
  fetchJobDetail,
  clearJobDetail,
  deleteJob,
  markJobPaid,
  undoManualPayment,
  cancelPaymentRequest,
  revokeContract,
  resetContractStatus,
  downloadContract,
  previewContractPdf,
} from "app/containers/Jobs/slice";
import { previewTemplatePdf } from "app/containers/ContractManagement/slice";
import { useJobSetupSend } from "app/containers/Jobs/hooks/useJobSetupSend";
import { toAppointmentShape } from "app/containers/Jobs/utils/toAppointmentShape";
import { formatAddress } from "app/containers/Jobs/utils/format";
import { openGoogleMaps } from "app/containers/JobCalendar/utils/mapUtils";
import JobBookingCard from "./cards/JobBookingCard";
import JobSetupCard from "./cards/JobSetupCard";
import ContractCard from "./cards/ContractCard";
import PaymentCard from "./cards/PaymentCard";
import InspectionResultCard from "./cards/InspectionResultCard";
import ReportCard from "./cards/ReportCard";
import EmailAutomationCard from "./cards/EmailAutomationCard";
import DeleteJobCard from "./cards/DeleteJobCard";
import "./jobDetail.css";

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    device: { isMobile },
  } = useApp();

  const {
    detail,
    detailJobId,
    getJobDetailStatus,
    getJobDetailError,
    deleteJobStatus,
  } = useSelector((state) => state.jobs);
  // Tier, Connect state, templates and the Save & Send handler are shared with
  // the calendar's Job Setup modal. This page fetches its own detail.
  const {
    tier,
    stripeConnected,
    stripeLoading,
    contractTemplates,
    showUpgrade,
    openUpgrade,
    closeUpgrade,
    setupModalProps,
  } = useJobSetupSend({ jobId: id, active: true });
  const {
    onSendTracks,
    contractEmailConnected,
    contractEmailLoading,
    contractEmailWarning,
  } = setupModalProps;
  // Mirrors the backend's resolve_send_template fallback (is_default row, then
  // the system template), so the card names the contract that would actually go
  // out rather than a hardcoded guess.
  const defaultTemplate = useMemo(
    () =>
      contractTemplates.find((tpl) => tpl.is_default) ||
      contractTemplates[0] ||
      null,
    [contractTemplates]
  );
  // A system-template send returns template_name: null, so the contract card
  // needs the computed system template's real name to render a heading.
  const systemTemplateName = useMemo(
    () => contractTemplates.find((tpl) => tpl.is_system)?.name || null,
    [contractTemplates]
  );

  const defaultTemplateName = defaultTemplate?.name || null;

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // "contract" | "payment" | null — which send is in flight, so the other card's
  // button is left alone.
  const [sendingKind, setSendingKind] = useState(null);
  // Mark as Paid / Undo / Cancel Request share one busy flag — only one of the
  // three is ever rendered for a given payment state.
  const [paymentBusy, setPaymentBusy] = useState(false);
  // Payment id awaiting cancel confirmation, or null.
  const [cancelTarget, setCancelTarget] = useState(null);
  // "revoke" | "reset" | null — which reason modal is open. Both take the same
  // body, so one modal serves both.
  const [reasonKind, setReasonKind] = useState(null);
  const [contractBusy, setContractBusy] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "default",
  });

  // Memoized so the modals' init effects don't reset on unrelated re-renders.
  const appointment = useMemo(
    () => (detail ? toAppointmentShape(detail) : null),
    [detail]
  );
  const lockState = useMemo(
    () => ({
      contractStatus: detail?.contract?.status,
      paymentStatus: detail?.payment?.status,
    }),
    [detail]
  );

  // Same gating the Job Setup modal uses, so the card buttons and the Save & Send
  // checkboxes can never disagree about whether a send is possible. Without this
  // the cards let you fire a request the backend is guaranteed to reject.
  const contractTrack = useMemo(
    () =>
      computeContractTrack({
        status: detail?.contract?.status,
        tier,
        appointment,
        quote: detail?.job_setup?.quote,
        emailConnected: contractEmailConnected,
        emailLoading: contractEmailLoading,
        emailWarning: contractEmailWarning,
      }),
    [
      detail,
      tier,
      appointment,
      contractEmailConnected,
      contractEmailLoading,
      contractEmailWarning,
    ]
  );
  const paymentTrack = useMemo(
    () =>
      computePaymentTrack({
        status: detail?.payment?.status,
        tier,
        stripeConnected,
        stripeLoading,
        appointment,
        quote: detail?.job_setup?.quote,
      }),
    [detail, tier, stripeConnected, stripeLoading, appointment]
  );

  useInitialFetch(() => dispatch(fetchJobDetail(id)), { skip: !id });

  useEffect(() => {
    dispatch(fetchCalendarSettings());
    dispatch(fetchSectionTemplates());
  }, [dispatch]);

  useEffect(() => () => dispatch(clearJobDetail()), [dispatch]);

  const goBack = useCallback(() => navigate("/jobs"), [navigate]);

  const refreshDetail = useCallback(() => {
    if (id) dispatch(fetchJobDetail(id));
  }, [dispatch, id]);

  // Close only. Both modals call onHide on Cancel and Esc as well as after a
  // save, so refetching here means every dismissed modal costs a full
  // GET /jobs/{id}/. The refresh hangs off onSaved instead.
  const handleCloseBooking = useCallback(() => setShowBookingModal(false), []);

  const handleCloseSetup = useCallback(() => setShowSetupModal(false), []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await dispatch(deleteJob(id)).unwrap();
      setShowDeleteModal(false);
      navigate("/jobs");
    } catch (error) {
      setShowDeleteModal(false);
      setToast({
        show: true,
        message: "Failed to delete job.",
        variant: "error",
      });
    }
  }, [dispatch, id, navigate]);

  // A non-"toggle" mode means the backend would reject the send, so route the
  // click to whatever would actually unblock the user instead of firing it.
  const handleBlockedTrack = useCallback(
    (mode) => {
      if (mode === "upgrade") {
        openUpgrade();
        return true;
      }
      if (mode === "stripe-hint") {
        setToast({
          show: true,
          message: "Connect Stripe to send payment requests.",
          variant: "default",
        });
        return true;
      }
      return false;
    },
    [openUpgrade]
  );

  const runSend = useCallback(
    async (kind) => {
      const isContract = kind === "contract";
      const track = isContract ? contractTrack : paymentTrack;
      if (handleBlockedTrack(track.mode)) return;

      // Tracked per kind so only the button that was clicked shows as busy — a
      // shared flag greys out both and reads as if both were clicked.
      setSendingKind(kind);
      const outcome = await onSendTracks({
        sendContract: isContract,
        sendPayment: !isContract,
      });
      setSendingKind(null);

      const [{ message, variant }] = buildSendMessages({
        result: outcome,
        wantContract: isContract,
        wantPayment: !isContract,
      });
      setToast({ show: true, message, variant });
      if (outcome[kind]?.ok) refreshDetail();
    },
    [
      contractTrack,
      paymentTrack,
      handleBlockedTrack,
      onSendTracks,
      refreshDetail,
    ]
  );

  const handleSendContract = useCallback(() => runSend("contract"), [runSend]);
  const handleSendPaymentRequest = useCallback(
    () => runSend("payment"),
    [runSend]
  );

  // Mark as Paid / Undo / Cancel Request all resolve to a toast plus a detail
  // refresh, so they share one runner. `busy` is a single flag because the three
  // are mutually exclusive — only one is ever on screen.
  const runPaymentAction = useCallback(
    async (thunk, arg, successMessage) => {
      setPaymentBusy(true);
      try {
        await dispatch(thunk(arg)).unwrap();
        setToast({ show: true, message: successMessage, variant: "success" });
        refreshDetail();
      } catch (message) {
        setToast({
          show: true,
          message:
            typeof message === "string" ? message : "Something went wrong.",
          variant: "error",
        });
      } finally {
        setPaymentBusy(false);
      }
    },
    [dispatch, refreshDetail]
  );

  const handleMarkPaid = useCallback(
    () => runPaymentAction(markJobPaid, { id }, "Marked as paid."),
    [runPaymentAction, id]
  );

  const handleUndoPayment = useCallback(
    (paymentId) =>
      runPaymentAction(undoManualPayment, paymentId, "Payment undone."),
    [runPaymentAction]
  );

  // Cancelling kills a link the client may already be looking at, so it is the
  // one payment action that confirms first.
  const handleConfirmCancel = useCallback(() => {
    const paymentId = cancelTarget;
    setCancelTarget(null);
    return runPaymentAction(
      cancelPaymentRequest,
      paymentId,
      "Payment request cancelled."
    );
  }, [runPaymentAction, cancelTarget]);

  const contractId = detail?.contract?.id;

  const runContractAction = useCallback(
    async (thunk, arg, successMessage) => {
      setContractBusy(true);
      try {
        await dispatch(thunk(arg)).unwrap();
        setToast({ show: true, message: successMessage, variant: "success" });
        refreshDetail();
      } catch (message) {
        setToast({
          show: true,
          message:
            typeof message === "string" ? message : "Something went wrong.",
          variant: "error",
        });
      } finally {
        setContractBusy(false);
      }
    },
    [dispatch, refreshDetail]
  );

  // Revoke and Reset differ only in endpoint and copy.
  const handleReasonConfirm = useCallback(
    async ({ reasonCode, note }) => {
      const isRevoke = reasonKind === "revoke";
      setReasonKind(null);
      await runContractAction(
        isRevoke ? revokeContract : resetContractStatus,
        { contractId, reasonCode, note },
        isRevoke ? "Contract revoked." : "Contract status reset."
      );
    },
    [reasonKind, contractId, runContractAction]
  );

  // No toast on success — the browser's own download is the feedback.
  const handleDownload = useCallback(async () => {
    setContractBusy(true);
    try {
      await dispatch(downloadContract(contractId)).unwrap();
    } catch (message) {
      setToast({
        show: true,
        message:
          typeof message === "string"
            ? message
            : "Couldn't download the contract.",
        variant: "error",
      });
    } finally {
      setContractBusy(false);
    }
  }, [dispatch, contractId]);

  // Opens a contract PDF in a new tab, so the inspector reads it in the
  // browser's own viewer rather than a lightbox we have to build and scroll.
  //
  // The tab is opened synchronously, before the fetch: window.open only survives
  // the popup blocker inside the click's user-gesture window, and by the time
  // the PDF bytes arrive that window has closed. `noopener` is deliberately NOT
  // passed — it makes window.open return null, and we need the handle to point
  // the tab at the blob once it resolves.
  const openPdfInNewTab = useCallback(async (fetchUrl, fallbackMessage) => {
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    setContractBusy(true);
    try {
      const url = await fetchUrl();
      // The object URL is intentionally not revoked: the new tab is still
      // reading from it, and the browser releases it when that tab unloads.
      if (tab) tab.location = url;
      else window.open(url, "_blank");
    } catch (message) {
      if (tab) tab.close();
      setToast({
        show: true,
        message: typeof message === "string" ? message : fallbackMessage,
        variant: "error",
      });
    } finally {
      setContractBusy(false);
    }
  }, []);

  // Before a send: the template that WOULD go out, so the inspector can read it
  // and then press Send Contract from the same card.
  const handlePreviewTemplate = useCallback(
    () =>
      defaultTemplate &&
      openPdfInNewTab(
        () =>
          dispatch(
            previewTemplatePdf({
              templateId: defaultTemplate.id,
              isSystem: Boolean(defaultTemplate.is_system),
            })
          ).unwrap(),
        "Couldn't preview this contract template."
      ),
    [dispatch, defaultTemplate, openPdfInNewTab]
  );

  // After a send: the contract the client actually received, re-rendered from
  // the send-time snapshot. Download can't answer this while the contract is
  // out for signature — it streams BoldSign's signed PDF and 404s until one
  // exists — so an unsigned contract has no other way to be looked at.
  const handlePreviewContract = useCallback(
    () =>
      contractId &&
      openPdfInNewTab(
        () => dispatch(previewContractPdf(contractId)).unwrap(),
        "Couldn't preview this contract."
      ),
    [dispatch, contractId, openPdfInNewTab]
  );

  const handleDeferred = useCallback((label) => {
    setToast({
      show: true,
      message: `${label} will be available when that feature launches.`,
      variant: "default",
    });
  }, []);

  // The calendar and the Jobs list also load details into this slice, so a
  // detail or error left over from another job must never paint as this one.
  const ownsDetail = String(detailJobId) === String(id);
  if (
    !ownsDetail ||
    getJobDetailStatus === "loading" ||
    (!detail && getJobDetailStatus !== "failed")
  ) {
    return <LoadingSpinner />;
  }

  if (getJobDetailStatus === "failed" || !detail) {
    return (
      <div className="job-detail-container">
        <div className="job-detail-error-state">
          <p>{getJobDetailError || "This job could not be found."}</p>
          <Button variant="outline-primary" onClick={goBack}>
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  const address = formatAddress(detail.address);

  return (
    <div className="job-detail-container">
      <button type="button" className="job-detail-back" onClick={goBack}>
        <Icon path={mdiArrowLeft} size={0.6} className="me-2" />
        Back to Jobs
      </button>

      <div className="job-detail-title-row">
        <h2 className="job-detail-address">{address}</h2>
        {address && (
          <Button
            variant="link"
            className="job-detail-nav-button"
            onClick={() => openGoogleMaps(address)}
            aria-label="Navigate to address"
          >
            <Icon path={mdiSend} size={0.75} className="job-detail-nav-icon" />
          </Button>
        )}
        <JobStatusBadge domain="inspection" value={detail.status} />
      </div>

      <div className="job-detail-cards">
        <JobBookingCard job={detail} onEdit={() => setShowBookingModal(true)} />
        <JobSetupCard job={detail} onEdit={() => setShowSetupModal(true)} />

        <div className="jd-card-row">
          <ContractCard
            job={detail}
            onEditTemplate={() => setShowSetupModal(true)}
            defaultTemplateName={defaultTemplateName}
            systemTemplateName={systemTemplateName}
            isFreeTier={tier === "Free"}
            onUpgrade={openUpgrade}
            onSendContract={handleSendContract}
            onRevoke={() => setReasonKind("revoke")}
            onResetStatus={() => setReasonKind("reset")}
            onDownload={handleDownload}
            onPreviewTemplate={handlePreviewTemplate}
            onPreviewContract={handlePreviewContract}
            busy={contractBusy}
            sendTrack={contractTrack}
            sending={sendingKind === "contract"}
          />
          <PaymentCard
            job={detail}
            tier={tier}
            onEditQuote={() => setShowSetupModal(true)}
            onSendPaymentRequest={handleSendPaymentRequest}
            onMarkPaid={handleMarkPaid}
            onCancelRequest={setCancelTarget}
            onUndoPayment={handleUndoPayment}
            sendTrack={paymentTrack}
            sending={sendingKind === "payment"}
            busy={paymentBusy}
          />
        </div>

        <div className="jd-card-row">
          <InspectionResultCard job={detail} />
          <ReportCard job={detail} onDeferred={handleDeferred} />
        </div>

        <EmailAutomationCard job={detail} onDeferred={handleDeferred} />
        <DeleteJobCard onDelete={() => setShowDeleteModal(true)} />
      </div>

      <AppointmentModal
        show={showBookingModal}
        onHide={handleCloseBooking}
        onSaved={refreshDetail}
        appointment={appointment}
        initialSlot={null}
        lockState={lockState}
        onNavigateToDetail={() => setShowBookingModal(false)}
      />

      <JobSetupModal
        show={showSetupModal}
        onHide={handleCloseSetup}
        appointment={appointment}
        {...setupModalProps}
        onSaved={refreshDetail}
        onNavigateToDetail={() => setShowSetupModal(false)}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteJobStatus === "loading"}
      />

      <ConfirmationModal
        show={Boolean(cancelTarget)}
        onHide={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
        title="Cancel payment request?"
        confirmLabel="Cancel Request"
        isLoading={paymentBusy}
        loadingLabel="Cancelling"
        staticWhileLoading
      >
        <p>
          The payment link stops working immediately. If your client already has
          it, they won't be able to pay from it.
        </p>
        <p className="mb-0">
          This job goes back to <strong>No Payment</strong>, so you can send a
          new request or record the payment yourself.
        </p>
      </ConfirmationModal>

      <ContractReasonModal
        show={Boolean(reasonKind)}
        onHide={() => setReasonKind(null)}
        onConfirm={handleReasonConfirm}
        isLoading={contractBusy}
        title={
          reasonKind === "reset"
            ? "Reset Morph contract status?"
            : "Revoke contract?"
        }
        description={
          reasonKind === "reset"
            ? "The signed copy stays with your client and at BoldSign — only Morph's status is cleared, so you can re-issue a contract that covers the changes."
            : "Your client's signing link stops working immediately. The job's booking fields unlock so you can correct them and send again."
        }
        confirmLabel={reasonKind === "reset" ? "Reset Status" : "Revoke"}
      />

      <UpgradePlanModal open={showUpgrade} onClose={closeUpgrade} />

      <CustomToast
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        message={toast.message}
        variant={toast.variant}
        isMobile={isMobile}
      />
    </div>
  );
};

// The detail is fetched once on mount, so moving between two /jobs/:id entries
// without leaving the page (browser back / forward) must remount it; otherwise
// it would wait forever on a detail that was never requested.
const JobDetailRoute = () => {
  const { id } = useParams();
  return <JobDetail key={id} />;
};

export default JobDetailRoute;
